/**
 * Kalkulační výpočet záložky „Calculation“ – čistý port serverového `CalculationMath` (backend). Metriky se
 * počítají živě při editaci tarifů, proto musí být logika identická se serverem: prodané osoby platí skutečnou
 * (přepočtenou) cenou, neprodaná místa tarifní cenou daného pásma; vše v domácí měně. Ceny pásem (v měně tarifu)
 * se násobí kurzem `tariffRate`.
 */
import type { CalculationTermDto } from '@/api/generated/schemas';

/** Editovatelné tarifní pásmo „od N. osoby“ (cena v měně tarifu). */
export interface TierDraft {
  count: number;
  price: number;
}

export interface TermMetrics {
  /** Výnos při plné kapacitě (domácí měna). */
  revenueFull: number;
  /** Náklad při plné kapacitě (domácí měna). */
  costFull: number;
  profit: number;
  /** Aktuální profit ze skutečně prodaných osob. */
  profitNow: number;
  marginPercent: number;
  /** Počet osob, od kterého je termín v zisku (null = nikdy). */
  breakeven: number | null;
  /** Cena prvního pásma v měně tarifu (sloupec „Cena 1.“). */
  firstTierPrice: number;
  /** Náklad na osobu (paxCalc + paxOnsite + paxTransport), domácí měna. */
  paxUnitCost: number;
  /** Skupinový náklad (groupCalc + groupOnsite + groupTransport), domácí měna. */
  groupCost: number;
  /** Náklad na osobu vč. podílu skupinového (TOT/os). */
  totPerPerson: number;
  /** Efektivní cena za osobu na pozici 1..kapacita (domácí měna). */
  effective: number[];
}

interface Band {
  from: number;
  to: number;
  price: number;
}

function buildBands(tiers: TierDraft[], capacity: number, tariffRate: number): Band[] {
  const bands: Band[] = [];
  let acc = 0;
  for (let i = 0; i < tiers.length; i++) {
    const isLast = i === tiers.length - 1;
    const count = isLast
      ? Math.max(0, capacity - acc)
      : Math.max(0, Math.min(tiers[i].count, capacity - acc));
    const from = acc + 1;
    acc += count;
    bands.push({ from, to: acc, price: tiers[i].price * tariffRate });
  }
  return bands;
}

function priceAtPosition(bands: Band[], n: number): number {
  for (const band of bands) {
    if (n >= band.from && n <= band.to) return band.price;
  }
  return bands.length > 0 ? bands[bands.length - 1].price : 0;
}

/** Spočte metriky termínu pro daná (efektivní) tarifní pásma a kurz tarifu. */
export function computeTermMetrics(
  term: CalculationTermDto,
  tiers: TierDraft[],
  tariffRate: number
): TermMetrics {
  const capacity = Math.max(0, term.capacity);
  const bands = buildBands(tiers, capacity, tariffRate);
  // Náklad = Cost Scheme (kalkulační i on-site) + doprava. Doprava přichází ze serveru už bez spojených
  // letů — ty jsou nepovinný příplatek a do kalkulace nevstupují.
  const paxUnitCost = term.paxCalc + term.paxOnsite + term.paxTransport;
  const groupCost = term.groupCalc + term.groupOnsite + term.groupTransport;
  const sold = term.soldPricesDomestic;
  const soldCount = Math.min(sold.length, capacity);

  const effective: number[] = [];
  for (let n = 1; n <= capacity; n++) {
    effective.push(n <= soldCount ? sold[n - 1] : priceAtPosition(bands, n));
  }

  let revenueFull = 0;
  for (const value of effective) revenueFull += value;

  const costFull = paxUnitCost * capacity + groupCost;
  const profit = revenueFull - costFull;

  let revenueNow = 0;
  for (const price of sold) revenueNow += price;
  const profitNow = revenueNow - (paxUnitCost * sold.length + groupCost);

  const marginPercent = revenueFull > 0 ? (profit / revenueFull) * 100 : 0;

  let breakeven: number | null = null;
  let cumulative = 0;
  for (let n = 1; n <= capacity; n++) {
    cumulative += effective[n - 1];
    if (cumulative - (paxUnitCost * n + groupCost) >= 0) {
      breakeven = n;
      break;
    }
  }

  const firstTierPrice = tiers.length > 0 ? tiers[0].price : 0;
  const totPerPerson = paxUnitCost + (capacity > 0 ? groupCost / capacity : 0);

  return {
    revenueFull,
    costFull,
    profit,
    profitNow,
    marginPercent,
    breakeven,
    firstTierPrice,
    paxUnitCost,
    groupCost,
    totPerPerson,
    effective,
  };
}

/** Jeden řádek PAX rozkladu – kumulativní výsledek při prodeji právě N osob. */
export interface PaxRow {
  n: number;
  price: number;
  cost: number;
  profit: number;
  margin: number;
  positive: boolean;
  isBreakeven: boolean;
  isCurrent: boolean;
}

/** Sestaví kumulativní PAX rozklad termínu z jeho metrik. */
export function buildPaxRows(term: CalculationTermDto, metrics: TermMetrics): PaxRow[] {
  const capacity = Math.max(0, term.capacity);
  const soldCount = Math.min(term.soldPricesDomestic.length, capacity);
  const rows: PaxRow[] = [];
  let cumulativeRevenue = 0;
  for (let n = 1; n <= capacity; n++) {
    cumulativeRevenue += metrics.effective[n - 1];
    const cumulativeCost = metrics.groupCost + n * metrics.paxUnitCost;
    const profit = cumulativeRevenue - cumulativeCost;
    const margin = cumulativeRevenue > 0 ? (profit / cumulativeRevenue) * 100 : 0;
    rows.push({
      n,
      price: metrics.effective[n - 1],
      cost: cumulativeCost,
      profit,
      margin,
      positive: profit >= 0,
      isBreakeven: n === metrics.breakeven,
      isCurrent: n === soldCount,
    });
  }
  return rows;
}

export interface AggregateMetrics {
  totalProfit: number;
  totalRevenue: number;
  totalCapacity: number;
  totalSold: number;
  profitableTerms: number;
  termsCount: number;
  marginPercent: number;
}

/** Agreguje metriky přes všechny termíny (souhrn „Všechny termíny“). */
export function aggregateMetrics(
  terms: CalculationTermDto[],
  metricsByTerm: Map<string, TermMetrics>
): AggregateMetrics {
  let totalProfit = 0;
  let totalRevenue = 0;
  let totalCapacity = 0;
  let totalSold = 0;
  let profitableTerms = 0;
  for (const term of terms) {
    const metrics = metricsByTerm.get(term.termGroupId);
    if (!metrics) continue;
    totalProfit += metrics.profit;
    totalRevenue += metrics.revenueFull;
    totalCapacity += term.capacity;
    totalSold += term.sold;
    if (metrics.profit >= 0) profitableTerms += 1;
  }
  return {
    totalProfit,
    totalRevenue,
    totalCapacity,
    totalSold,
    profitableTerms,
    termsCount: terms.length,
    marginPercent: totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0,
  };
}
